import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Users, Target, Heart } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Reveal, Stagger, StaggerItem } from '../components/motion/Reveal';
import { Counter } from '../components/motion/Counter';
import { Seo } from '../components/Seo';
import { seoPages } from '../seo';

const stats = [
  { value: 10, label: { sl: 'Let izkušenj', en: 'Years of Experience', it: 'Anni di Esperienza' } },
  { value: 1000, label: { sl: 'Zadovoljnih strank', en: 'Happy Customers', it: 'Clienti Soddisfatti' } },
  { value: 5000, label: { sl: 'Zaključenih projektov', en: 'Completed Projects', it: 'Progetti Completati' } }
];

const About = () => {
  const { t } = useLanguage();

  const values = [
    {
      icon: Award,
      title: { sl: 'Kakovost', en: 'Quality', it: 'Qualità' },
      description: {
        sl: 'Uporabljamo najsodobnejše tehnologije in materiale za najboljše rezultate.',
        en: 'We use the latest technologies and materials for the best results.',
        it: 'Utilizziamo le più recenti tecnologie e materiali per i migliori risultati.'
      }
    },
    {
      icon: Users,
      title: { sl: 'Partnerstvo', en: 'Partnership', it: 'Partnership' },
      description: {
        sl: 'Z vsakim stranko gradimo dolgoročno partnerstvo, ki temelji na zaupanju.',
        en: 'We build long-term partnerships with each client based on trust.',
        it: 'Costruiamo partnership a lungo termine con ogni cliente basate sulla fiducia.'
      }
    },
    {
      icon: Target,
      title: { sl: 'Natančnost', en: 'Precision', it: 'Precisione' },
      description: {
        sl: 'Vsaka podrobnost šteje - pozorni smo na najmanjše detajle.',
        en: 'Every detail matters - we pay attention to the smallest details.',
        it: 'Ogni dettaglio conta - prestiamo attenzione ai minimi particolari.'
      }
    },
    {
      icon: Heart,
      title: { sl: 'Strast', en: 'Passion', it: 'Passione' },
      description: {
        sl: 'Tiskanje je naša strast - vsak projekt je izziv, ki ga radi sprejmemo.',
        en: 'Printing is our passion - every project is a challenge we gladly accept.',
        it: 'La stampa è la nostra passione - ogni progetto è una sfida che accettiamo volentieri.'
      }
    }
  ];

  return (
    <div className="min-h-screen bg-[#000000] pt-32 pb-24">
      <Seo title={seoPages.about.title} description={seoPages.about.description} />
      <div className="container mx-auto px-5 md:px-10">
        {/* Hero Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-24">
          <Reveal>
            <h1 className="heading-2 text-[white] mb-6">
              {t({ sl: 'O NAS', en: 'ABOUT US', it: 'CHI SIAMO' })}
            </h1>
            <div className="space-y-4 text-[white]">
              <p className="body-large">
                {t({
                  sl: 'Matrix Print je lokalna tiskarna v Kopru z večletnimi izkušnjami na področju profesionalnega tiska.',
                  en: 'Matrix Print is a local print shop in Koper with years of experience in professional printing.',
                  it: 'Matrix Print è una tipografia locale a Capodistria con anni di esperienza nella stampa professionale.'
                })}
              </p>
              <p className="body-medium text-[#b0b0b0]">
                {t({
                  sl: 'Naša zgodba se je začela z idejo, da vsako podjetje in posameznik zasluži kakovosten tisk po dostopni ceni. Danes ponosno služimo številnim zadovoljnim strankam po vsej Sloveniji.',
                  en: 'Our story began with the idea that every business and individual deserves quality printing at an affordable price. Today we proudly serve numerous satisfied customers throughout Slovenia.',
                  it: 'La nostra storia è iniziata con l\'idea che ogni azienda e individuo merita una stampa di qualità a un prezzo accessibile. Oggi serviamo con orgoglio numerosi clienti soddisfatti in tutta la Slovenia.'
                })}
              </p>
              <p className="body-medium text-[#b0b0b0]">
                {t({
                  sl: 'Ne prodajamo samo tiska - pomagamo vam oživeti vaše ideje in jih spremeniti v otipljivo resničnost. Od prvega stika do predaje končnega izdelka vam stojimo ob strani s profesionalnim svetovanjem.',
                  en: "We don't just sell printing - we help you bring your ideas to life and turn them into tangible reality. From first contact to delivery of the final product, we stand by you with professional advice.",
                  it: 'Non vendiamo solo stampa - vi aiutiamo a dare vita alle vostre idee e trasformarle in realtà tangibile. Dal primo contatto alla consegna del prodotto finale, vi assistiamo con consulenza professionale.'
                })}
              </p>
            </div>
          </Reveal>
          <Reveal className="relative h-[500px] rounded-lg overflow-hidden" delay={0.15} y={40}>
            <img
              src="/assets/about-printer.webp"
              alt="Matrix Print workspace"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#000000] via-transparent to-transparent opacity-40" />
          </Reveal>
        </div>

        {/* Values Section */}
        <div className="mb-24">
          <Reveal>
            <h2 className="heading-4 text-[white] text-center mb-12">
              {t({ sl: 'NAŠE VREDNOTE', en: 'OUR VALUES', it: 'I NOSTRI VALORI' })}
            </h2>
          </Reveal>
          <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="values-grid">
            {values.map((value) => {
              const IconComponent = value.icon;
              return (
                <StaggerItem key={value.title.en} className="h-full">
                <Card className="bg-[#1a1a1a] border-[rgba(255, 255, 255, 0.1)] text-center team-card h-full">
                  <CardHeader>
                    <div className="mx-auto w-16 h-16 rounded-full bg-[white] flex items-center justify-center mb-4">
                      <IconComponent className="w-8 h-8 text-[#000000]" />
                    </div>
                    <CardTitle className="text-[white] text-lg">
                      {t(value.title)}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-[#b0b0b0] body-small">
                      {t(value.description)}
                    </p>
                  </CardContent>
                </Card>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>

        {/* Stats Section */}
        <Reveal className="bg-[#1a1a1a] border border-[rgba(255, 255, 255, 0.1)] rounded-lg p-12 mb-24" y={40} data-testid="stats-section">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            {stats.map((stat) => (
              <div key={stat.value}>
                <div className="text-[white] text-5xl font-bold mb-2" style={{ fontFamily: "'Exo 2', sans-serif" }}>
                  <Counter value={stat.value} suffix="+" data-testid={`stat-counter-${stat.value}`} />
                </div>
                <p className="text-[#b0b0b0] body-medium">{t(stat.label)}</p>
              </div>
            ))}
          </div>
        </Reveal>

        {/* CTA */}
        <Reveal className="text-center">
          <h3 className="heading-4 text-[white] mb-6">
            {t({
              sl: 'Želite postati del naše zgodbe?',
              en: 'Want to become part of our story?',
              it: 'Volete far parte della nostra storia?'
            })}
          </h3>
          <p className="body-large text-[#b0b0b0] mb-8 max-w-2xl mx-auto">
            {t({
              sl: 'Stopite v stik z nami in skupaj ustvarimo nešto izjemnega.',
              en: 'Get in touch with us and together we will create something exceptional.',
              it: 'Contattateci e insieme creeremo qualcosa di eccezionale.'
            })}
          </p>
          <Link to="/contact">
            <Button className="btn-primary min-w-[200px]">
              {t({ sl: 'Kontaktirajte nas', en: 'Contact Us', it: 'Contattaci' })}
            </Button>
          </Link>
        </Reveal>
      </div>
    </div>
  );
};

export default About;
